import React, { useEffect, useState } from 'react';
import { string, func, bool } from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import mixpanel from 'mixpanel-browser';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
// import { ensureListing, ensureUser } from '../../util/data';
// import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import { stringFromLength } from '../../util/data';

const { Money } = sdkTypes;
const MIN_LENGTH_FOR_LONG_WORDS = 10;
import treeImg from '../../../src/assets/images/tree.png'

export const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const Img = ({ src, fallback, alt, className }) => (
  <img
    src={src}
    alt={alt || ''}
    className={className}
    loading="lazy"
    onError={fallback ? e => { e.currentTarget.onerror = null; e.currentTarget.src = fallback; } : undefined}
  />
);

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const [verificationStatus, setVerificationStatus] = useState({ verifications: [] });

  const {
    className,
    rootClassName,
    intl,
    isCompany,
    currentUser,
    hit: listing,
    renderSizes,
    hasImgRadius,
    listingConfig,
    onSearchListings
    // showAuthorInfo,
    // setActiveListing,
  } = props;

  const classes = classNames(rootClassName || css.root, className, hasImgRadius && css.companycard);
  const {
    user,
    title = '',
    price,
    skills,
    objectID,
    addSelfie1,
    experiences,
    uploadVideoInPhotoes,
    roles,
    // location,
    // _geoloc,
    // description,
    // educations,
    listingType,
    // higherEducation,
    // availabilityPlan,
  } = listing || {};
  let companyimage = (listing && listing.user && listing.user.companyImage) || '';
  let minRateValue = Infinity;
  let maxRateValue = -Infinity;

  const isUserAdmin = (listing && listing.user && listing.user.isAdminAccount) || false;
  const boardMember = (roles && roles.length && Array.isArray(roles) && roles[0]?.boardMember) || false;


  if (roles && roles.length && listingType === 'company') {
    roles.forEach(role => {
      if (role.perHourRate && role.perHourRate.label) {
        let rateLabel = role.perHourRate.label;
        let rates = rateLabel.match(/\d+/g).map(Number); // Extract numerical values

        rates.forEach(rate => {
          if (rate < minRateValue) {
            minRateValue = rate;
          }
          if (rate > maxRateValue) {
            maxRateValue = rate;
          }
        });
      } else {
        minRateValue = maxRateValue = 0
      }
    });
  }

  let minRateLabel = `CA$${minRateValue}`;
  let maxRateLabel = `$${maxRateValue}`;

  const {
    location,
    mainProfession,
    availableHoursPerWeek,
  } = user || {};
  const id = objectID;
  const { timezone: userTimezone } = location || {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
  } = config.layout.listingImage;
  function getInitials(name) {
    return name?.split(' ').map(word => word?.charAt(0)?.toUpperCase())?.join('');
  }

  const initials = getInitials(title);
  const slug = createSlug(initials || "");

  // Get raw image URL (same logic as TalentCard)
  const rawImgUrl = companyimage
    || uploadVideoInPhotoes?.replace(/\.(mp4|mkv|wmv|m4v|mov|avi|flv|webm|flac|mka|m4a|aac|ogg)$/i, '.png')
    || addSelfie1;

  const imgFallback = listing?.addSelfie1Fallback || treeImg;

  // For Cloudinary URLs inject high-quality transformation params (from TalentCard)
  const imgUrl = rawImgUrl?.includes('res.cloudinary.com')
    ? rawImgUrl.replace('/upload/', '/upload/q_auto:best,f_auto,w_800/')
    : rawImgUrl;

  const { formattedPrice, priceTitle } = price ? priceData(new Money(price.amount, price.currency), config.currency, intl) : {};
  const yearOfExperience = experiences && experiences.length && experiences[0].inputValues
    ? moment().diff(moment(parseInt(experiences[0].inputValues.yearOfExperience.key), 'YYYY'), 'year')
    : null;

  useEffect(() => {
    const data = listing?.verificationData || [];
    const completed = data?.filter(v =>
      ['In Progress', 'Complete',].includes(v.status)
    );
    const newStatus = {
      count: completed?.length,
      verifications: completed?.map(v => v.name),
    };
    setVerificationStatus(prev =>
      JSON.stringify(prev) === JSON.stringify(newStatus) ? prev : newStatus
    );
  }, [listing]);



  return (id && slug && !isUserAdmin ?
    <div className={classes} onClick={() => {
      mixpanel.track((isCompany ? 'JobPage' : 'ProPage'), {
        distinct_id: currentUser?.id?.uuid,
        user_id: currentUser?.id?.uuid,
        userType: isCompany ? 'company' : 'fractionalPro',
        environment: (process.env && process.env.REACT_APP_ENV) || 'development',
      });
    }}>

      <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
        <div
          className={css.listingBox}
        >
          <div
            className={css.borderRadiusMinimal}
          >
            <div className={css.cardContentMinimal}>
              <div className={css.minimal_imgWrap}>
                <Img src={imgUrl || imgFallback} fallback={imgFallback} alt={'pro'} className={css.photo} />
              </div>

              <div className={css.cardData}>
                <div className={listingType === 'company' ? null : css.cardInfo}>
                  {initials
                    ? <div className={css.mainTitle}>{(listingType && listingType == 'company') ? title : null}
                    </div>
                    : null}
                  {experiences && experiences.length
                    ? <h5>
                      {experiences[0].label}
                    </h5>
                    : null}

                  {verificationStatus?.count > 0 && (
                    <div className={css.verificationBadge}>
                      <span>Verified</span>
                      <div className={css.tooltip}>
                        <ul className={css.verificationList}>
                          {verificationStatus?.verifications?.map((name, index) => (
                            <li key={index} className={css.verificationItem}>
                              {index + 1}.  {name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {listingConfig && listingConfig.companyIndustry
                    ? <div className={css.designation}>{listingConfig.companyIndustry.find(ind => ind.option === mainProfession)?.label}</div>
                    : null}
                  <div className={css.listingList}>
                    {yearOfExperience ? <div className={css.listItem}>{yearOfExperience}{yearOfExperience > 1 ? ' years' : ' year'} of experience</div> : null}
                    {availableHoursPerWeek ? <div className={css.listItem}>{availableHoursPerWeek}h per {experiences && experiences.length && experiences[0]?.boardMember ? 'month' : 'week'} available</div> : null}
                    <div className={css.listItem}>
                      {userTimezone
                        ? <FormattedMessage id="EditListingHowForm.timeZoneWithTime" values={{
                          timeZone: moment().tz(userTimezone).format('z')
                        }} />
                        : null}
                    </div>
                  </div>
                </div>
                {listingType === "company" ? (
                  <div className={css.listingList}>
                    {roles?.[0]?.perHourRate?.label ? (
                      <div className={css.listItem}>
                        {(() => {
                          const label = roles[0].perHourRate.label; // "CA$150-200"
                          const suffix = boardMember ? 'per month' : 'per hour';

                          const [min, max] = label.replace('CA$', '').split('-');

                          return `CAD $${min} - $${max} ${suffix}`;
                        })()}
                      </div>
                    ) : price?.amount ? (
                      <div className={css.listItem}>
                        {`CAD $${price.amount / 100} ${boardMember ? 'per month' : 'per hour'}`}
                      </div>
                    ) : null}


                    {(roles && roles?.length && roles?.[0]?.availableHoursPerWeek?.label) ? (
                      <div className={css.listItem}>
                        {roles?.[0].availableHoursPerWeek?.label} {boardMember ? 'Monthly' : 'Weekly'}
                      </div>
                    ) : null}

                    {roles && roles.length ? <div className={css.listItem}>Start {roles[0].startDate ? new Date(roles[0].startDate?.date).toLocaleDateString() : roles[0].start}</div> : null}

                    {listing?.workType ? (
                      <div className={css.listItem}>
                        {listing?.workType === 'flexible'
                          ? "Flexible to anything"
                          : listing?.workType.charAt(0).toUpperCase() + listing?.workType.slice(1)}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          {/* {skills && skills.length
            ? <div className={css.listingBottomBox}>
              {skills.map(({ key, label }) => (
                <span key={key}>
                  {label}
                </span>))}
            </div>
            : null} */}
        </div>
        {listingType == 'company' ? <div className={css.info}>
          <div className={css.price}>
            <div className={css.priceValue} title={priceTitle}>
              {(listingType && listingType == 'company') ? (maxRateLabel != '$0') ? `${minRateLabel}-${maxRateLabel} ` : null : `${formattedPrice} `}
              {/* {formattedPrice} */}
              {maxRateLabel != '$0' ? (roles && roles.length && roles[0]?.boardMember) ? "per month" : "per hour" : null}
            </div>
          </div>
          {/* <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div> */}
        </div> : null}
      </NamedLink>
    </div>
    : null
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  // listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);